import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Card, Field, Input, Logo, Select } from "@/design-system";
import { useAuth } from "@/lib/auth";

const loginSchema = z.object({
  email: z.string().email("Email notoʻgʻri"),
  password: z.string().min(1, "Parolni kiriting"),
});
const registerSchema = z.object({
  full_name: z.string().trim().min(2, "Ismingizni yozing"),
  business_name: z.string().trim().min(2, "Biznes nomini yozing"),
  business_kind: z.enum(["dokon", "onlayn", "it", "xizmat", "startap", "umumiy"]),
  email: z.string().email("Email notoʻgʻri"),
  password: z.string().min(8, "Kamida 8 belgi"),
});

const KINDS = [
  { value: "dokon", label: "Doʻkon" },
  { value: "onlayn", label: "Onlayn savdo" },
  { value: "xizmat", label: "Xizmat koʻrsatish" },
  { value: "it", label: "IT va xizmatlar" },
  { value: "startap", label: "IT mahsulot / startap" },
  { value: "umumiy", label: "Boshqa biznes" },
];

const PROOF = [
  ["Kassa", "chek, ombor va pul bitta tranzaksiyada"],
  ["Nasiya daftari", "kim, qancha, qachon — unutilmaydi"],
  ["ESF va soliq", "Didox, E-IMZO, kalendar"],
  ["Payme · Click · 1C", "toʻlov va bank koʻchirmasi avtomatik"],
];

function AuthLayout({ title, sub, children, footer }) {
  return (
    <div className="auth">
      <aside className="auth-brand" aria-hidden="true">
        <Logo onDark height={28} />
        <div className="auth-brand-body">
          <p className="auth-claim">
            Har soʻm —<br />
            <span>rasidli.</span>
          </p>
          <ul className="auth-proof">
            {PROOF.map(([k, v]) => (
              <li key={k}>
                <b>{k}</b>
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="auth-ledger rs-num">
          <span>23.10 · Chek CHK-000146</span>
          <span>+273 500</span>
          <span>23.10 · Ijara</span>
          <span>−3 000 000</span>
          <span>22.10 · Dilshod aka, nasiya</span>
          <span>+135 000</span>
        </div>
      </aside>
      <div className="auth-form">
        <div className="auth-card">
          <span className="auth-logo-sm">
            <Logo height={26} />
          </span>
          <div>
            <h1>{title}</h1>
            <p className="rs-muted" style={{ margin: "6px 0 0" }}>
              {sub}
            </p>
          </div>
          <Card>{children}</Card>
          <p className="rs-muted" style={{ margin: 0, fontSize: 14 }}>
            {footer}
          </p>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(loginSchema) });
  const e = /** @type {any} */ (formState.errors);
  return (
    <AuthLayout
      title="Kirish"
      sub="Har soʻm — rasidli."
      footer={
        <>
          Hisobingiz yoʻqmi? <Link to="/register">Roʻyxatdan oʻting</Link>
        </>
      }
    >
      <form
        className="stack"
        noValidate
        onSubmit={handleSubmit(async (v) => {
          setError("");
          try {
            await login(v.email, v.password);
            nav("/", { replace: true });
          } catch (err) {
            setError(/** @type {Error} */ (err).message);
          }
        })}
      >
        {error && <Alert tone="xato" title={error} />}
        <Field label="Email" htmlFor="email" error={e.email?.message}>
          <Input id="email" type="email" autoComplete="email" {...register("email")} />
        </Field>
        <Field label="Parol" htmlFor="password" error={e.password?.message}>
          <Input id="password" type="password" autoComplete="current-password" {...register("password")} />
        </Field>
        <Button variant="primary" type="submit" block loading={formState.isSubmitting}>
          Kirish
        </Button>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { register: signUp } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const { register, handleSubmit, formState } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: { business_kind: "dokon" },
  });
  const e = /** @type {any} */ (formState.errors);
  return (
    <AuthLayout
      title="Biznesingizni oching"
      sub="Kassa, ombor, pul va hujjatlar — bir joyda."
      footer={
        <>
          Hisobingiz bormi? <Link to="/login">Kiring</Link>
        </>
      }
    >
      <form
        className="stack"
        noValidate
        onSubmit={handleSubmit(async (v) => {
          setError("");
          try {
            await signUp(v);
            nav("/", { replace: true });
          } catch (err) {
            setError(/** @type {Error} */ (err).message);
          }
        })}
      >
        {error && <Alert tone="xato" title={error} />}
        <Field label="Ismingiz" htmlFor="full_name" error={e.full_name?.message}>
          <Input id="full_name" autoComplete="name" {...register("full_name")} />
        </Field>
        <Field label="Biznes nomi" htmlFor="business_name" error={e.business_name?.message}>
          <Input id="business_name" placeholder="Masalan: Barakali Market" {...register("business_name")} />
        </Field>
        <Field
          label="Biznes turi"
          htmlFor="business_kind"
          hint="Menyu shu turga moslanadi — keyin oʻzgartirsa boʻladi"
        >
          <Select id="business_kind" options={KINDS} {...register("business_kind")} />
        </Field>
        <Field label="Email" htmlFor="remail" error={e.email?.message}>
          <Input id="remail" type="email" autoComplete="email" {...register("email")} />
        </Field>
        <Field label="Parol" htmlFor="rpass" error={e.password?.message} hint="Kamida 8 belgi">
          <Input id="rpass" type="password" autoComplete="new-password" {...register("password")} />
        </Field>
        <Button variant="primary" type="submit" block loading={formState.isSubmitting}>
          Boshlash
        </Button>
      </form>
    </AuthLayout>
  );
}
